import React, { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { rsvpMeta } from "../mock";
import useReveal from "../hooks/useReveal";
import { submitRSVP } from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export default function RSVP() {
  const [ref, visible] = useReveal();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    attending: "yes",
    guests: "0",
    meal: "",
    notes: "",
    song_request: "",
    song_artist: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    if (form.attending === "yes" && !form.meal) {
      toast.error("Please choose a meal preference.");
      return;
    }
    setLoading(true);
    try {
      await submitRSVP(form);
      setSubmitted(true);
      toast.success("Your RSVP has been received. Thank you!");
    } catch (err) {
      toast.error("Could not submit RSVP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="rsvp" className="py-24 md:py-32 px-6">
      <div
        ref={ref}
        className={`max-w-[1150px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center reveal ${
          visible ? "is-visible" : ""
        }`}
      >
        <div>
          <h2 className="font-serif-display text-[#9E2B25] text-[46px] md:text-[64px] leading-none">
            {rsvpMeta.title}
          </h2>
          <p className="mt-6 text-[#4a443d] leading-relaxed max-w-md">{rsvpMeta.body}</p>

          {submitted ? (
            <div className="mt-10 bg-[#F9F5EE] border border-[#211E1A]/10 p-10 rounded-sm text-center">
              <div className="w-16 h-16 rounded-full bg-[#9E2B25] text-[#F4EFE6] flex items-center justify-center mx-auto">
                <Check size={30} />
              </div>
              <h3 className="mt-6 font-serif-display text-4xl text-[#211E1A]">
                {form.attending === "yes" ? "We can’t wait to celebrate!" : "You’ll be missed"}
              </h3>
              <p className="mt-3 text-[#4a443d] leading-relaxed max-w-sm mx-auto">
                {form.attending === "yes"
                  ? `Thank you, ${form.full_name.split(" ")[0]} — your RSVP has been recorded. See you on the shores of Lake Como.`
                  : `Thank you for letting us know, ${form.full_name.split(" ")[0]}. We’ll raise a glass to you from Lake Como.`}
              </p>
              <p className="mt-6 font-serif-display text-2xl text-[#9E2B25]">With love, Adam &amp; Sara</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-10 space-y-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-[11px] uppercase tracking-widest-2 text-[#211E1A] font-semibold">
                    Full Name *
                  </label>
                  <input
                    value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    placeholder="John Smith"
                    className="mt-2 w-full bg-transparent border-b border-[#211E1A]/25 py-2.5 text-[#211E1A] placeholder:text-[#9c9488] focus:outline-none focus:border-[#9E2B25] transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest-2 text-[#211E1A] font-semibold">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="guest@email.com"
                    className="mt-2 w-full bg-transparent border-b border-[#211E1A]/25 py-2.5 text-[#211E1A] placeholder:text-[#9c9488] focus:outline-none focus:border-[#9E2B25] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-widest-2 text-[#211E1A] font-semibold">
                  Will you be attending? *
                </label>
                <div className="mt-3 flex gap-8">
                  {[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }].map((opt) => (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => set("attending", opt.v)}
                      className="flex items-center gap-2 group"
                    >
                      <span
                        className={`w-4 h-4 border rounded-[3px] flex items-center justify-center transition-colors ${
                          form.attending === opt.v
                            ? "bg-[#9E2B25] border-[#9E2B25]"
                            : "border-[#211E1A]/40"
                        }`}
                      >
                        {form.attending === opt.v && <Check size={12} className="text-[#F4EFE6]" />}
                      </span>
                      <span className="text-[#211E1A]">{opt.l}</span>
                    </button>
                  ))}
                </div>
              </div>

              {form.attending === "yes" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[11px] uppercase tracking-widest-2 text-[#211E1A] font-semibold">
                      Meal Choice *
                    </label>
                    <Select value={form.meal} onValueChange={(v) => set("meal", v)}>
                      <SelectTrigger className="mt-2 rounded-sm border-[#211E1A]/25 bg-[#F9F5EE]">
                        <SelectValue placeholder="Select meal..." />
                      </SelectTrigger>
                      <SelectContent>
                        {rsvpMeta.meals.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest-2 text-[#211E1A] font-semibold">
                      Additional Guests
                    </label>
                    <Select value={form.guests} onValueChange={(v) => set("guests", v)}>
                      <SelectTrigger className="mt-2 rounded-sm border-[#211E1A]/25 bg-[#F9F5EE]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["0", "1", "2", "3"].map((g) => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {form.attending === "yes" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[11px] uppercase tracking-widest-2 text-[#211E1A] font-semibold">
                      Song Request
                    </label>
                    <input
                      value={form.song_request}
                      onChange={(e) => set("song_request", e.target.value)}
                      placeholder="Song title"
                      className="mt-2 w-full bg-transparent border-b border-[#211E1A]/25 py-2.5 text-[#211E1A] placeholder:text-[#9c9488] focus:outline-none focus:border-[#9E2B25] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest-2 text-[#211E1A] font-semibold">
                      Artist
                    </label>
                    <input
                      value={form.song_artist}
                      onChange={(e) => set("song_artist", e.target.value)}
                      placeholder="Artist name"
                      className="mt-2 w-full bg-transparent border-b border-[#211E1A]/25 py-2.5 text-[#211E1A] placeholder:text-[#9c9488] focus:outline-none focus:border-[#9E2B25] transition-colors"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-[11px] uppercase tracking-widest-2 text-[#211E1A] font-semibold">
                  Additional Information
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={3}
                  placeholder="Hey, I'd like to let the couple know..."
                  className="mt-2 w-full bg-[#F9F5EE] border border-[#211E1A]/20 rounded-sm p-3 text-[#211E1A] placeholder:text-[#9c9488] focus:outline-none focus:border-[#9E2B25] transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#211E1A] text-[#F4EFE6] text-[12px] uppercase tracking-widest-2 py-4 rounded-sm hover:bg-[#9E2B25] transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </form>
          )}
        </div>

        <div className="img-zoom h-[520px] md:h-[680px] overflow-hidden border-[6px] border-[#F4EFE6] shadow-xl">
          <img src={rsvpMeta.image} alt="RSVP" className="w-full h-full object-cover" />
        </div>
      </div>
    </section>
  );
}
